import { formatDistanceToNow } from 'date-fns';
import { formatPhoneDisplay } from '@/lib/contacts/phone';
import type {
  FormInstanceItem,
  FormInstanceSection,
  UploadedFileAnswer,
} from '@/types/formsEngine';
import type { LeadFieldTarget } from './lead-field-targets';
import {
  formatAddressLines,
  formatTime12h,
  isUploadedFileAnswer,
  uploadedFilesOf,
} from './field-types';

export const EMPTY_ANSWER = '—';

export interface FormattedAnswer {
  key: string;
  label: string;
  /** Display text. Long text keeps its line breaks; render with whitespace-pre-line. */
  value: string;
  multiline: boolean;
  photoCount: number;
  /** FILE_UPLOAD answers: the files, each with a short-lived `url` when the server sent one. */
  files?: UploadedFileAnswer[];
  /** The lead field this question is mapped to (`item.config.leadField`), for a lead's Form tab. */
  leadField?: LeadFieldTarget;
}

export interface FormattedAnswerSection {
  key: string;
  name: string;
  answers: FormattedAnswer[];
}

function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/** True when an item carries an answer worth showing (a value or a photo). */
export function hasAnswer(item: Pick<FormInstanceItem, 'value' | 'photos'>): boolean {
  return !isBlank(item.value) || (item.photos?.length ?? 0) > 0;
}

function formatCalendarDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  // A calendar date has no time zone: build it locally so it never shifts a day.
  const date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

function formatBoolean(item: Pick<FormInstanceItem, 'type'>, value: boolean): string {
  if (item.type === 'PASS_FAIL') return value ? 'Pass' : 'Fail';
  return value ? 'Yes' : 'No';
}

/** One answer as display text. Blank answers render as an em dash. */
export function formatAnswerValue(
  item: Pick<FormInstanceItem, 'type' | 'value' | 'config'>,
): string {
  const { value } = item;
  if (isBlank(value)) return EMPTY_ANSWER;

  switch (item.type) {
    case 'CHECKBOX':
    case 'YES_NO':
    case 'PASS_FAIL':
      return typeof value === 'boolean' ? formatBoolean(item, value) : safeJson(value);
    case 'TEXT':
      // Phone answers are stored as +1 and ten digits; show them the way the
      // rest of the platform shows a phone number.
      if (typeof value === 'string' && item.config?.inputType === 'tel') {
        return formatPhoneDisplay(value);
      }
      return typeof value === 'string' ? value : safeJson(value);
    case 'BARCODE_SCAN':
    case 'SINGLE_SELECT':
      return typeof value === 'string' ? value : safeJson(value);
    case 'NUMBER': {
      if (typeof value !== 'number') return safeJson(value);
      const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 6 }).format(value);
      return item.config?.unit ? `${formatted} ${item.config.unit}` : formatted;
    }
    case 'MULTI_SELECT':
      return Array.isArray(value) ? value.map(String).join(', ') : safeJson(value);
    case 'DATE':
      return typeof value === 'string' ? formatCalendarDate(value) : safeJson(value);
    case 'MEASUREMENT': {
      const measurement = value as { value?: unknown; unit?: unknown };
      return typeof measurement.value === 'number'
        ? `${measurement.value}${typeof measurement.unit === 'string' ? ` ${measurement.unit}` : ''}`
        : safeJson(value);
    }
    case 'TIME':
      return typeof value === 'string' ? formatTime12h(value) : safeJson(value);
    case 'ADDRESS': {
      const lines = formatAddressLines(value);
      return lines.length ? lines.join('\n') : safeJson(value);
    }
    case 'FILE_UPLOAD': {
      if (!Array.isArray(value)) return safeJson(value);
      const count = value.filter(isUploadedFileAnswer).length;
      return `${count} ${count === 1 ? 'file' : 'files'}`;
    }
    case 'SIGNATURE': {
      const signature = value as { signerName?: unknown };
      return typeof signature.signerName === 'string' && signature.signerName.trim()
        ? `Signed by ${signature.signerName}`
        : 'Signed';
    }
    default:
      return typeof value === 'string' ? value : safeJson(value);
  }
}

/**
 * Every answered item, grouped by section, in template order. Unanswered items
 * are skipped, and so is a section with nothing answered.
 */
export function formatSubmissionAnswers(sections: FormInstanceSection[]): FormattedAnswerSection[] {
  return [...sections]
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((section) => ({
      key: `${section.sectionId}:${section.repeatKey}`,
      name: section.repeatLabel ? `${section.name} · ${section.repeatLabel}` : section.name,
      answers: [...section.items]
        .sort((left, right) => left.sortOrder - right.sortOrder)
        .filter(hasAnswer)
        .map((item) => ({
          key: item.itemId,
          label: item.label,
          value: isBlank(item.value) ? EMPTY_ANSWER : formatAnswerValue(item),
          leadField: item.config?.leadField,
          multiline:
            (item.type === 'TEXT' && typeof item.value === 'string' && /\n/.test(item.value)) ||
            (item.type === 'ADDRESS' && formatAddressLines(item.value).length > 1),
          photoCount: item.photos?.length ?? 0,
          ...(item.type === 'FILE_UPLOAD' && Array.isArray(item.value)
            ? { files: uploadedFilesOf(item.value) }
            : {}),
        })),
    }))
    .filter((section) => section.answers.length > 0);
}

/** Submitted time for a tooltip / detail row, e.g. "Sep 16, 2026, 3:04 PM". */
export function formatSubmittedExact(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function formatSubmittedRelative(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return formatDistanceToNow(date, { addSuffix: true });
}
